import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { Description } from "../components/Description.tsx";
import { flyToBag } from "../components/fly.ts";
import { Icon } from "../components/Icon.tsx";
import { Mockup } from "../components/Mockup.tsx";
import { findVariant, initialSelection, OptionPicker, Quantity } from "../components/Options.tsx";
import { useConfig } from "../components/Shell.tsx";
import { Sticker } from "../components/Sticker.tsx";
import { ApiError, api, call } from "../lib/api.ts";
import { cartStore } from "../lib/cart.ts";
import { money } from "../lib/format.ts";
import { toast } from "../lib/toast.ts";
import { useStudioData } from "./home.tsx";
import { NotFoundPage } from "./misc.tsx";

export function SharePage() {
  const { shareId = "" } = useParams();
  const cfg = useConfig();
  const currency = cfg.data?.currency ?? "EUR";
  const { bases } = useStudioData();
  const q = useQuery({
    queryKey: ["share", shareId],
    queryFn: () => call(api.designs.share[":shareId"].$get({ param: { shareId } })),
  });
  const [selected, setSelected] = useState<Record<string, string> | null>(null);
  const [qty, setQty] = useState(1);
  const [tried, setTried] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);

  if (q.error instanceof ApiError && q.error.status === 404) return <NotFoundPage what="design" />;
  const d = q.data?.design;
  const base = bases.data?.baseProducts.find((b) => b.id === d?.baseProductId);
  if (!d || !bases.data)
    return (
      <div className="sheet pdp">
        <div className="skeleton" style={{ aspectRatio: "1" }} />
      </div>
    );

  const sel = selected ?? (base ? initialSelection(base.variants) : {});
  const variant = base ? findVariant(base.variants, sel) : undefined;
  const garment =
    variant ??
    base?.variants.find((v) => Object.entries(sel).every(([k, val]) => v.options[k] === val));
  const aspect = { width: 1000, height: Math.round(1000 / (d.previewAspect ?? 1)) };

  const add = () => {
    setTried(true);
    if (!variant || !base) return;
    cartStore.add({
      line: { kind: "design", designId: d.id, baseVariantId: variant.id, quantity: qty },
      title: d.title,
      variantTitle: `${base.name} · ${variant.title}`,
      imageUrl: d.previewUrl,
      unitPriceCents: variant.priceCents,
      href: `/d/${d.shareId}`,
    });
    flyToBag(stageRef.current, d.previewUrl);
    toast("Added to your bag", { action: { label: "View bag", href: "/bag" } });
  };

  return (
    <div className="sheet pdp">
      <div className="pdp__media" ref={stageRef}>
        <Sticker face="var(--yolk)" tilt={-2} peelable>
          {base && d.previewUrl ? (
            <Mockup
              spec={base.mockup}
              color={garment?.swatch}
              fit={base.printFit}
              aspect={aspect}
              label={`${d.title} on a ${base.name}`}
              className="pdp__mockup"
            >
              <image href={d.previewUrl} width={aspect.width} height={aspect.height} />
            </Mockup>
          ) : (
            d.previewUrl && <img src={d.previewUrl} alt={d.title} className="pdp__img" />
          )}
        </Sticker>
      </div>
      <div className="pdp__info">
        <p className="muted">Made in the memeup Studio</p>
        <h1 className="pdp__title">{d.title}</h1>
        {!d.shareable && d.isOwner && (
          <div className="notice" data-tone="info">
            Only you can see this page until we've checked your image. You can still order it.
          </div>
        )}
        {base ? (
          <>
            <p className="pdp__price price">
              {money(variant?.priceCents ?? base.fromPriceCents, currency)}
            </p>
            <Description html={base.descriptionHtml} className="pdp__desc" />
            <OptionPicker variants={base.variants} selected={sel} onSelect={setSelected} />
            {tried && !variant && (
              <p className="form-error" role="alert">
                Pick a size first.
              </p>
            )}
            <div className="pdp__buy">
              <Quantity value={qty} onChange={setQty} label="Quantity" />
              <button type="button" className="btn pdp__add" onClick={add}>
                <Icon name="bag" /> Add to bag
              </button>
            </div>
          </>
        ) : (
          <div className="notice" data-tone="info">
            This product isn't available right now, but you can remix the design onto something
            else.
          </div>
        )}
        <div className="remix">
          <Link to={`/make?remix=${d.shareId}`} className="btn btn--white">
            <Icon name="pen" /> {d.isOwner ? "Edit this design" : "Remix it with your own words"}
          </Link>
          <button
            type="button"
            className="link-btn"
            onClick={async () => {
              const url = window.location.href;
              if (navigator.share) await navigator.share({ url, title: d.title }).catch(() => {});
              else {
                await navigator.clipboard.writeText(url);
                toast("Link copied");
              }
            }}
          >
            <Icon name="link" size={16} /> Share
          </button>
        </div>
      </div>
    </div>
  );
}
